import {
  BookGoodsPicker } from "./BookGoodsPicker";
import { CoverFramePicker } from "./CoverFramePicker";
import { coverImageStyle,
  arSceneKeysIn,
  bookDeletePrompt,
  bookIdOfAssetKey,
  orphanBooks,
  recoveredPages,
  recoverSay,
} from "@greg-chick/shared";
import { saveBookDraft, loadBookDraft, clearBookDraft, draftAgeText, type BookDraft } from "../lib/bookDraft";
import { normalizeBookGoods } from "@greg-chick/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  normalizeAspect,
  DEFAULT_ASPECT,
  bookCoverKey,
  bookPageImageKey,
  bookAudioKey,
  bookAudioLegacyKey,
  coverPageIdOf,
  normalizeBlurb,
  BLURB_MAX,
  type LayoutPos as Pos,
  type AssetSpec,
} from "@greg-chick/shared";
import {
  loadBookDoc,
  loadManifest,
  loadSlots,
  listBooks,
  publishBook,
  readPageText,
  absArtUrl,
  deleteBook,
  publishAssets,
  type BookDoc,
  type BookInfo,
  type Manifest,
  type ManifestEntry,
} from "../lib/contentApi";
import { SlotUploader } from "./SlotUploader";
import { useRegisterSave } from "../lib/saveBar";
import { QrPrint } from "./QrPrint";
import { NarrationSection, PageAspect, PageArSection } from "./BookPageParts";
import { saveFailSay } from "../lib/saveFail";

const newBookId = () => `book_${Date.now().toString(36)}`;

type TextKind = "motion" | "subtitle";
type Page = Record<string, unknown>;

const DEFAULT_POS: Record<TextKind, Pos> = {
  motion: { x: 0.82, y: 0.16 },
  subtitle: { x: 0.5, y: 0.9 },
};
const clamp = (n: number) => Math.max(0, Math.min(1, n));

export function relPos(el: HTMLElement, cx: number, cy: number): Pos {
  const r = el.getBoundingClientRect();
  return { x: clamp((cx - r.left) / r.width), y: clamp((cy - r.top) / r.height) };
}

/** 기존 id 와 겹치지 않는 새 페이지 id(pNN). */
/**
 * 안 쓰는 쪽 id 를 만든다. `want` 를 주면 그 이름을 먼저 써 본다(표지는 `cover` 가 읽기 좋다).
 * 쪽 id 는 그림 키(`book.page.<책>.<쪽>`)에 그대로 들어가므로 겹치면 그림이 뒤섞인다.
 */
/** 책 문서에서 표지 담는 값만 뽑는다 — 미리보기 두 곳이 **같은 값**을 보게 하려고. */
function coverStyleOf(b: BookDoc | null): { coverFit?: "cover" | "contain"; coverPos?: { x: number; y: number } } {
  return {
    coverFit: (b?.coverFit as "cover" | "contain" | undefined) ?? undefined,
    coverPos: (b?.coverPos as { x: number; y: number } | undefined) ?? undefined,
  };
}

export function freshPageId(pages: Page[], want?: string): string {
  const used = new Set(pages.map((p) => String(p.id)));
  if (want && !used.has(want)) return want;
  let n = pages.length + 1;
  let id = `p${String(n).padStart(2, "0")}`;
  while (used.has(id)) {
    n += 1;
    id = `p${String(n).padStart(2, "0")}`;
  }
  return id;
}

// 전자책 만들기(FR-ADMIN-16): 제목 → 페이지(추가·삭제·순서) → 페이지별 그림·동영상·자막·모션·위치.
// 출력=book.json(웹 normalizeManifest) + asset-manifest(book.page.<id> 이미지). 게시 시 둘 다 반영.
export function BookEditor() {
  const [books, setBooks] = useState<BookInfo[]>([]);
  const [bookId, setBookId] = useState<string>("");
  const [book, setBook] = useState<BookDoc | null>(null);
  const [manifest, setManifest] = useState<Manifest>({ version: 0, assets: {} });
  const [spec, setSpec] = useState<AssetSpec | null>(null);
  const [coverSpec, setCoverSpec] = useState<AssetSpec | null>(null);
  const [videoSpec, setVideoSpec] = useState<AssetSpec | null>(null);
  const [audioSpec, setAudioSpec] = useState<AssetSpec | null>(null);
  const [idx, setIdx] = useState(0);
  const [drag, setDrag] = useState<TextKind | null>(null);
  /**
   * 캔버스를 **어느 말로 볼지**. 의성어는 말마다 길이가 딴판이라(「쿵!」 vs 「Thump, thump!」)
   * 자리도 따로 정할 수 있어야 한다 — 지금 보고 있는 말의 자리가 옮겨진다.
   */
  const [previewLang, setPreviewLang] = useState<"ko" | "en">("ko");
  const [msg, setMsg] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  /**
   * 목록 정렬. `shelf` 는 **아이가 보는 책장 차례** — 여기서만 ↑↓ 로 바꿀 수 있다.
   * 최근순·이름순은 운영자가 찾기 편하려고 보는 것이고, 아이 화면과는 상관없다.
   */
  const [sort, setSort] = useState<"shelf" | "recent" | "title">("shelf");
  /** 차례를 바꾸는 중이면 여기 담긴다(저장 전까지 목록만 바뀐다). null = 손 안 댐. */
  const [shelf, setShelf] = useState<string[] | null>(null);
  const [dirty, setDirty] = useState(false); // 저장하지 않은 변경 있음
  const [ocrBusy, setOcrBusy] = useState(false); // 그림에서 글자 읽는 중(이른 return 위 — 훅 순서)
  /** 이 기기에 저장해 둔 작업(있으면 배너로 알리고, 누르면 불러온다). */
  const [draft, setDraft] = useState<BookDraft | null>(null);
  const [busy, setBusy] = useState(false); // 저장/삭제 진행 중(중복 실행 방지)
  const [undo, setUndo] = useState<{ doc: BookDoc; label: string; id: string } | null>(null); // 삭제 되돌리기
  const canvasRef = useRef<HTMLDivElement>(null);

  // 저장 안 된 변경이 있으면 새로고침·창닫기 경고(데이터 유실 방지)
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  useEffect(() => {
    void Promise.all([listBooks(), loadManifest(), loadSlots()]).then(async ([list, m, slots]) => {
      setBooks(list);
      setManifest(m);
      setSpec(slots.find((s) => s.key === "book.page")?.spec ?? null);
      setCoverSpec(slots.find((s) => s.key === "book.cover")?.spec ?? null);
      setVideoSpec(slots.find((s) => s.key === "book.pagevideo")?.spec ?? null);
      setAudioSpec(slots.find((s) => s.key === "book.audio")?.spec ?? null);
      if (list.length > 0) {
        setBookId(list[0]!.id);
        setBook(await loadBookDoc(list[0]!.id));
        setDraft(loadBookDraft(list[0]!.id));
      } else {
        const id = newBookId();
        setBookId(id);
        setBook({ id, title_ko: "", pages: [] });
      }
    });
  }, []);

  // ⚠️ 훅은 **이른 return 위에** 있어야 한다 — 아래에 두면 책이 로드되는 순간
  //    훅 개수가 달라져 React 가 "Rendered more hooks than during the previous render" 로 터진다.
  // 저장은 화면 아래 고정 바 하나로(어느 화면이든 같은 자리·같은 버튼).
  /**
   * 💾 웹에 올리지 않고 하던 일만 저장. 그림·소리는 **올리는 순간 이미 엣지에 있으므로**
   * 여기엔 주소만 담긴다(브라우저에 큰 파일을 쟁이지 않는다).
   */
  async function saveDraft() {
    if (!book) return;
    // 이 책과 관련된 그림 자리만 담는다 — 매니페스트 전체를 담으면 저장소가 금방 찬다
    const mine: Record<string, ManifestEntry> = {};
    for (const [k, v] of Object.entries(manifest.assets)) {
      if (k.includes(`.${bookId}.`) || k === bookCoverKey(bookId)) mine[k] = v;
    }
    if (!saveBookDraft(bookId, { ...book, id: bookId }, mine)) {
      throw new Error("저장 실패");
    }
    setDraft(loadBookDraft(bookId));
  }
  useRegisterSave({ id: "book", label: "전자책", save: publish, saveDraft });
  // 책장 차례는 **따로 등록**한다 — 책 하나를 고치는 것과 차례를 바꾸는 것은 다른 일이고,
  // 바꾼 게 있을 때만(dirty>0) 저장 바가 켜져 운영자가 무엇을 올리는지 안다.
  useRegisterSave({
    id: "bookshelf",
    label: "책장 차례",
    dirty: shelf ? 1 : 0,
    save: publishShelf,
  });

  // ⚠️ 훅은 **이른 return 위에** — 아래에 두면 책이 로드되는 순간 훅 개수가 달라져 터진다
  /**
   * 🛟 **되살릴 수 있는 책** — 올린 파일은 남았는데 목록에서 사라진 것.
   *
   * 운영에 실제로 있었다: 표지 1 · 본문 10쪽 · **읽어주기 20개**. 한 사람이 한나절 넘게
   * 만든 책 한 권이었는데, 관리자가 할 수 있는 말은 「다시 만들거나 지우세요」뿐이었다 —
   * 다시 만들려면 31개를 **다시 올려야** 한다. 그럴 바엔 아무도 안 한다.
   *
   * 🔴 **책 목록을 못 받았으면 아무것도 안 보여 준다.** 「못 받았다」를 「책이 없다」로 읽으면
   * 멀쩡한 책이 전부 후보로 뜨고, 누르면 지금 책을 덮어쓴다(`releaseReady` 와 같은 이유).
   * 목록은 첫 로드에서 한 번 받으므로, 한 권이라도 있으면 받은 것이다 — 매니페스트도 마찬가지다.
   */
  const lost = useMemo(
    () =>
      books.length > 0 && Object.keys(manifest.assets).length > 0
        ? orphanBooks(
            Object.keys(manifest.assets),
            new Set(books.map((b) => b.id)),
            bookIdOfAssetKey,
          )
        : [],
    [books, manifest],
  );

  if (!book) return <p className="hint">전자책 불러오는 중…</p>;

  const pages = book.pages as Page[];
  const safeIdx = Math.min(idx, Math.max(0, pages.length - 1));
  const page = (pages[safeIdx] ?? {}) as Page;
  const pageId = String(page.id ?? `p${safeIdx}`);
  // 페이지 미디어 키는 전자책 id 로 네임스페이스(책마다 페이지 id 가 같아도 충돌 없음)
  // 자리 이름은 **만드는 곳에서 가져온다**(shared) — 손으로 짜면 뷰어와 갈린다
  const imageKey = bookPageImageKey(bookId, pageId);
  const videoKey = `book.pagevideo.${bookId}.${pageId}`;
  const audioKoKey = bookAudioKey(bookId, pageId, "ko");
  const audioEnKey = bookAudioKey(bookId, pageId, "en");
  // 구버전 비네임스페이스 키(book.page.<pageId> 등) — 웹 loadBook 과 동일하게 폴백으로 읽는다.
  // (예: 이미지는 네임스페이스인데 동영상만 옛 키 book.pagevideo.cover 로 저장된 경우 관리자에 안 보이던 버그)
  // 옛 이름(책 이름 없는 것)도 shared 가 만든다 — 폴백 규칙이 뷰어와 같아야 한다
  const legacy = {
    image: `book.page.${pageId}`,
    video: `book.pagevideo.${pageId}`,
    ko: bookAudioLegacyKey(pageId, "ko"),
    en: bookAudioLegacyKey(pageId, "en"),
  };
  // 정식 키 우선, 없으면 레거시 키. 편집/삭제 시 setMedia 가 정식 키로 이관하며 레거시 정리.
  const mediaEntry = (nsKey: string, legacyKey: string) => manifest.assets[nsKey] ?? manifest.assets[legacyKey];
  const setMedia = (nsKey: string, legacyKey: string) => (_k: string, entry: ManifestEntry | null) => {
    setEntry(nsKey, entry);
    if (manifest.assets[legacyKey]) setEntry(legacyKey, null); // 레거시 → 정식 이관
  };
  const imageUrl = (typeof page.image === "string" ? page.image : undefined) ?? mediaEntry(imageKey, legacy.image)?.url;
  const aspect = normalizeAspect(page.aspect) ?? DEFAULT_ASPECT; // 페이지 비율(가로/세로)
  const motionKo = String(page.motionText ?? "");
  const motionEn = String(page.motionText_en ?? "");
  // 영어를 안 적었으면 한글을 그대로 보여준다 — 아이 화면과 같은 규칙
  const motionText = previewLang === "en" ? motionEn || motionKo : motionKo;
  const subtitle = String(page.subtitle_ko ?? "");
  const video = String(page.video ?? "");
  const hasVideoFile = !!mediaEntry(videoKey, legacy.video);
  /**
   * 🔴 **자리를 안 정한 것과 정한 것은 아이 화면에서 다르게 보인다.**
   *
   * 아이 화면은 자리가 있으면 그림 **위**에 얹고, 없으면 자막을 그림 **아래**에 둔다.
   * 그런데 이 편집기는 자리가 없을 때도 기본값(0.5, 0.9)에 **그림 위로** 그렸다 —
   * 운영자는 「위에 있네」로 보고 넘어가는데 아이는 아래에서 본다.
   * (첫 화면 캐릭터에서 똑같은 것을 고쳤다 — 「안 정했을 때 어떻게 되나」도 판정이다.)
   *
   * 끌 수 있어야 하니 자리는 기본값에 그리되, **안 정했음을 눈에 보이게** 표시한다.
   */
  const subtitlePlaced = !!(page.subtitlePos as Pos | undefined);
  const motionPlaced = !!(page.motionPos as Pos | undefined);
  const motionPosKo = (page.motionPos as Pos | undefined) ?? DEFAULT_POS.motion;
  // 영어 자리를 안 정했으면 한글 자리를 그대로(아이 화면과 같은 규칙)
  const motionPos =
    previewLang === "en" ? ((page.motionPos_en as Pos | undefined) ?? motionPosKo) : motionPosKo;
  const subtitlePos = (page.subtitlePos as Pos | undefined) ?? DEFAULT_POS.subtitle;

  /**
   * 목록 썸네일용 표지 — 웹 고르기 화면과 같은 순서(전용 표지 → 표지 페이지 그림).
   *
   * 🔴 **`??` 가 아니라 `||`.** [준비 지우기] 로 비운 자리는 남고 주소만 빈다(`{ url: "" }`) —
   * `??` 는 빈 문자열을 값으로 쳐서 **멀쩡한 표지 페이지 그림을 가린다.** 그러면 웹에는
   * 표지가 나오는데 관리자에서만 안 보인다(웹은 `firstOnWeb` 으로 고쳐 뒀다).
   */
  function libCover(b: BookInfo): string | undefined {
    const page = b.coverPageId;
    return (
      manifest.assets[bookCoverKey(b.id)]?.url ||
      (page ? manifest.assets[bookPageImageKey(b.id, page)]?.url : undefined)
    );
  }

  function setEntry(key: string, entry: ManifestEntry | null) {
    setDirty(true);
    setManifest((m) => {
      const assets = { ...m.assets };
      if (entry) assets[key] = entry;
      else delete assets[key];
      return { ...m, assets };
    });
  }

  function mutateBook(patch: Record<string, unknown>) {
    setDirty(true);
    setBook((b) => (b ? { ...b, ...patch } : b));
  }
  function mutatePage(patch: Record<string, unknown>) {
    setDirty(true);
    setBook((b) => {
      if (!b) return b;
      const next = (b.pages as Page[]).slice();
      next[safeIdx] = { ...next[safeIdx], ...patch };
      return { ...b, pages: next };
    });
  }
  function addPage() {
    setDirty(true);
    setBook((b) => {
      if (!b) return b;
      const list = (b.pages as Page[]).slice();
      const id = freshPageId(list);
      list.push({ id, subtitle_ko: "" });
      setIdx(list.length - 1);
      return { ...b, pages: list };
    });
  }
  /**
   * 🖼️ **표지 페이지 만들기** — 맨 앞에 `cover: true` 페이지를 하나 넣는다.
   *
   * ## 왜 버튼이 필요한가
   * 고르기 화면의 표지는 ①전용 표지(`book.cover.<책>`) ②**`cover` 로 표시한 페이지 그림**
   * 순으로 정해진다(`coverPageIdOf`). 그런데 ②를 만들 방법이 화면에 없었다 — 페이지를 추가한 뒤
   * 어딘가에서 표지로 지정해야 하는데, 그 표시가 눈에 띄지 않았다.
   *
   * 표지는 **맨 앞**에 넣는다. 아이가 책을 펼치면 표지부터 보는 게 자연스럽고, 순서를 나중에
   * 옮기게 하면 "표지가 3쪽에 있는 책"이 생긴다.
   *
   * 이미 표지가 있으면 새로 만들지 않고 **그 페이지로 데려간다** — 표지가 둘인 책은 없다.
   */
  function addCoverPage() {
    const list = pages.slice();
    const existing = list.findIndex((p) => (p as Page).cover === true);
    if (existing >= 0) {
      setIdx(existing);
      setMsg("이미 표지 페이지가 있어요 — 그 페이지를 열었어요.");
      return;
    }
    setDirty(true);
    setBook((b) => {
      if (!b) return b;
      const next = (b.pages as Page[]).slice();
      const id = freshPageId(next, "cover");
      next.unshift({ id, subtitle_ko: "", cover: true });
      setIdx(0);
      return { ...b, pages: next };
    });
    setMsg("표지 페이지를 맨 앞에 넣었어요. 그림을 올리면 고르기 화면 표지로 쓰여요.");
  }

  /**
   * 🔤 **그림에서 글자 읽기** — 이 페이지 그림을 보고 자막·의성어를 채운다.
   *
   * ## 채우기만 하고 저장하지 않는다
   * 모델은 글자를 잘못 읽고 없는 말을 지어내기도 한다. 그래서 **칸에만 넣는다** —
   * 운영자가 눈으로 고친 뒤 [웹에 올리기]를 눌러야 아이 화면에 나간다.
   *
   * ## 이미 쓴 글은 먼저 물어본다
   * 자막을 손으로 다 써 둔 페이지에서 눌렀다가 통째로 덮이면 되돌릴 방법이 없다.
   * 비어 있는 칸만 채우는 것도 생각했지만, 그러면 "고쳐 쓰려고 눌렀는데 안 바뀐다"가 된다 —
   * 물어보고 **운영자가 정하게** 한다.
   */
  async function readTextFromImage() {
    if (ocrBusy || !book) return;
    const p = pages[safeIdx] as Page | undefined;
    if (!p) return;
    const filled = [p.subtitle_ko, p.subtitle_en, p.motionText, p.motionText_en].some(
      (v) => typeof v === "string" && v.trim(),
    );
    if (filled && !window.confirm("이 페이지에 이미 쓴 글이 있어요. 읽은 것으로 바꿀까요?")) return;

    setOcrBusy(true);
    setMsg("그림에서 글자를 읽는 중…");
    try {
      const r = await readPageText(bookId, String(p.id), title);
      if (!r.found) {
        setMsg("그림에서 글자를 찾지 못했어요. 글이 없는 그림이면 정상이에요.");
        return;
      }
      setDirty(true);
      mutatePage({
        subtitle_ko: r.subtitle_ko,
        subtitle_en: r.subtitle_en,
        motionText: r.motionText_ko,
        motionText_en: r.motionText_en,
      });
      setMsg("읽어서 채웠어요. 확인하고 고친 뒤 [웹에 올리기]를 눌러 주세요.");
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setOcrBusy(false);
    }
  }

  function deletePage(i: number) {
    const list = pages.slice();
    const target = list[i] as Page | undefined;
    if (!target) return;
    if (!window.confirm(`${i + 1}쪽(${String(target.id)})을 삭제할까요? 페이지 그림·동영상도 함께 지워집니다.`)) return;
    setDirty(true);
    const pid = String(target.id);
    for (const k of [
      bookPageImageKey(bookId, pid), `book.pagevideo.${bookId}.${pid}`,
      bookAudioKey(bookId, pid, "ko"), bookAudioKey(bookId, pid, "en"),
      // 구버전 비네임스페이스 키도 함께 정리
      `book.page.${pid}`, `book.pagevideo.${pid}`,
      bookAudioLegacyKey(pid, "ko"), bookAudioLegacyKey(pid, "en"),
    ]) setEntry(k, null);
    // AR(페이지 통합) 자산도 함께 정리 — 고정 키 + Spine 텍스처(멀티페이지, ar.spine.<ai>.tex.*)
    const ai = `${bookId}.${String(target.id)}`;
    // 🔴 지우는 목록이 **만드는 목록보다 좁으면 파일이 남는다** — 같은 곳에서 받는다.
    //    층 그림(`ar.layer.img.*`, docs/build/35)·Spine 텍스처는 개수를 모르므로 고정 목록에
    //    없다. 여기서 접두사로 따로 모으면 다음 종류가 생길 때 또 빠진다 — shared 가 **전부** 모은다.
    for (const k of arSceneKeysIn(Object.keys(manifest.assets), ai)) setEntry(k, null);
    list.splice(i, 1);
    setBook((b) => (b ? { ...b, pages: list } : b));
    setIdx((cur) => Math.max(0, Math.min(cur, list.length - 1)));
  }
  function movePage(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= pages.length) return;
    setDirty(true);
    const list = pages.slice();
    [list[i], list[j]] = [list[j]!, list[i]!];
    setBook((b) => (b ? { ...b, pages: list } : b));
    setIdx(j);
  }

  function onMove(e: React.PointerEvent) {
    if (!drag || !canvasRef.current) return;
    const p = relPos(canvasRef.current, e.clientX, e.clientY);
    // 지금 보고 있는 말의 자리를 옮긴다 — 한글을 보며 끌었는데 영어가 움직이면 안 된다
    if (drag !== "motion") return mutatePage({ subtitlePos: p });
    mutatePage(previewLang === "en" ? { motionPos_en: p } : { motionPos: p });
  }

  /**
   * 📚 **책장 차례 바꾸기** — 아이가 책을 고르는 화면의 순서.
   *
   * 저장하기 전까지는 **목록만** 바뀐다. 아이 화면에 반영되는 건 [웹에 올리기] 때다 —
   * 차례를 만지작거리는 동안 아이 화면이 계속 흔들리면 안 된다.
   *
   * 검색 중에는 못 바꾼다(`!q` 조건) — 걸러진 목록에서 옮기면 **안 보이는 책들과의 차례**가
   * 어떻게 되는지 운영자가 알 수 없다.
   */
  function moveBook(id: string, dir: -1 | 1) {
    const base = shelf ?? books.map((b) => b.id);
    const i = base.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= base.length) return;
    const next = [...base];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setShelf(next);
  }

  /**
   * 차례를 게시한다 — 책마다 `order` 를 넣어 다시 올린다.
   *
   * 차례만 담는 문서를 따로 두지 않는다(표·엔드포인트가 하나 더 생긴다). 책 문서에 숫자 하나를
   * 얹는 편이 싸고, 책을 지우면 그 차례도 같이 사라져 뒤에 남는 쓰레기가 없다.
   *
   * 저장소에만 있던 책은 이때 게시본이 생긴다 — 내용은 그대로 두고 차례만 얹는다.
   */
  async function publishShelf() {
    if (!shelf) return;
    for (let i = 0; i < shelf.length; i++) {
      const id = shelf[i]!;
      const doc = id === bookId ? { ...book, id } : await loadBookDoc(id);
      await publishBook(id, { ...(doc as BookDoc), id, order: i });
    }
    setBooks(await listBooks());
    setShelf(null);
  }

  /** 저장 안 된 변경이 있으면 확인 후에만 진행(데이터 유실 방지). */
  function confirmDiscard(): boolean {
    return !dirty || window.confirm("저장하지 않은 변경이 있어요. 버리고 이동할까요?");
  }

  async function switchBook(id: string) {
    if (id === bookId || busy) return;
    if (!confirmDiscard()) return;
    setMsg(null);
    setIdx(0);
    setBookId(id);
    setBook(await loadBookDoc(id));
    setDirty(false);
    setDraft(loadBookDraft(id)); // 저장해 둔 작업이 있으면 배너로 알린다
  }
  function createBook() {
    if (!confirmDiscard()) return;
    const id = newBookId();
    setBookId(id);
    setIdx(0);
    setBook({ id, title_ko: "", pages: [] });
    setDirty(false);
    setMsg("새 전자책 — 제목·페이지를 채우고 저장하세요.");
  }
  async function removeBook(id: string, label: string) {
    if (busy) return;
    /**
     * 🔴 **책 문서만 지워지고 그림·소리는 남는다** — 일부러 그렇다(아래 되돌리기 때문).
     * 그런데 아무도 그걸 몰랐다: 지운 뒤 파일은 R2 에 그대로 남고 화면 어디에도 표시가 없다.
     * 실제로 운영에 31개가 그 상태로 남아 있었다(2026-08-28). 지우는 순간이 알려 줄 유일한 때다.
     * 세는 규칙은 shared 한 곳(`bookIdOfAssetKey`) — [🚀 출시 준비]가 세는 것과 같은 수다.
     */
    const staying = Object.keys(manifest.assets).filter((k) => bookIdOfAssetKey(k) === id).length;
    if (!window.confirm(bookDeletePrompt(label || id, staying))) return;
    setBusy(true);
    // 되돌리기용으로 현재 문서 캐시(서버 삭제는 비가역이라 재게시로 복구)
    let snapshot: BookDoc | null = null;
    try {
      snapshot = await loadBookDoc(id);
    } catch {
      /* 캐시 실패해도 삭제는 진행 */
    }
    try {
      await deleteBook(id);
    } catch (e) {
      setBusy(false);
      setMsg(saveFailSay(e, "삭제"));
      return;
    }
    const rest = await listBooks();
    setBooks(rest);
    if (id === bookId) {
      if (rest.length > 0) {
        setBookId(rest[0]!.id);
        setIdx(0);
        setBook(await loadBookDoc(rest[0]!.id));
        setDirty(false);
      } else {
        createBook();
      }
    }
    if (snapshot) setUndo({ doc: snapshot, label: label || id, id });
    setMsg("🗑 전자책을 삭제했어요.");
    setBusy(false);
  }
  async function undoDelete() {
    if (!undo || busy) return;
    setBusy(true);
    try {
      await publishBook(undo.id, undo.doc);
      setBooks(await listBooks());
      setMsg(`↩ '${undo.label}' 전자책을 되살렸어요.`);
      setUndo(null);
    } catch (e) {
      setMsg(saveFailSay(e, "되살리기"));
    }
    setBusy(false);
  }

  /** 🔴 못 올렸으면 던진다 — 저장 바는 던지지 않으면 「✅ 웹에 올렸어요」를 띄운다(2026-09-18, App.publish 와 같은 이유). */
  async function publish() {
    if (!book) return; // 고른 책이 없다 — 올릴 것도 없다
    if (busy) throw new Error("이미 올리는 중이에요");
    if (!title.trim()) {
      setMsg("전자책 제목을 먼저 입력하세요.");
      throw new Error("전자책 제목이 비었어요 — 안 올렸어요");
    }
    setBusy(true);
    setMsg("저장 중…");
    try {
      const doc = { ...book, id: bookId };
      const [b, a] = await Promise.all([publishBook(bookId, doc), publishAssets(manifest.assets)]);
      setMsg(`✅ '${b.title}' 저장 v${b.version} · ${b.pages}쪽 (이미지 v${a.version}) — 홈/뷰어에 실시간 반영`);
      setBooks(await listBooks());
      setDirty(false);
      // 웹에 올라갔으니 보관해 둘 이유가 없다 — 남겨 두면 다음에 옛 작업을 권하게 된다
      clearBookDraft(bookId);
      setDraft(null);
    } catch (e) {
      // 🔴 이유를 삼키지 않는다 — 판정은 한 곳(saveFailSay). 저장 바에도 실패로 알린다.
      const say = saveFailSay(e);
      setMsg(say);
      setBusy(false);
      throw new Error(say);
    }
    setBusy(false);
  }

  /**
   * 되살린다 — **화면에 열어 주기만** 하고 웹에는 안 올린다.
   * 운영자가 쪽 순서를 보고, 제목을 적고, 자막을 뽑은 뒤에 스스로 올린다.
   */
  function recoverBook(id: string) {
    const found = lost.find((b) => b.id === id);
    if (!found) return;
    setBookId(id);
    setBook({ id, title_ko: "", pages: recoveredPages(found) });
    setDirty(true);
    // 🔴 제목이 비어 있어 [웹에 올리기]가 막힌다 — 그게 맞다. 무엇을 해야 하는지 말해 준다
    setMsg(`🛟 ${found.pageIds.length}쪽을 불러왔어요. ${recoverSay(found)}`);
  }

  function exportBook() {
    const blob = new Blob([JSON.stringify(book, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "book.json";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const title = String(book.title_ko ?? "");
  const access = String(book.access ?? "full");
  const previewPages = Number(book.previewPages ?? 3);
  const inList = books.some((b) => b.id === bookId);

  // 표지(고르기 화면) — 전용 표지가 있으면 그것, 없으면 '표지로 사용' 페이지 그림이 자동.
  // 규칙은 packages/shared/bookMeta 단일 정의(웹 고르기 화면이 쓰는 것과 같은 함수).
  const coverKey = bookCoverKey(bookId);
  const coverPage = coverPageIdOf(pages);
  const coverEntry = manifest.assets[coverKey];
  const coverFallbackUrl = coverPage ? manifest.assets[bookPageImageKey(bookId, coverPage)]?.url : undefined;
  // 🔴 `??` 는 비운 자리(`{ url: "" }`)를 값으로 쳐서 폴백을 가린다 — `||` 로 본다
  const coverUrl = coverEntry?.url || coverFallbackUrl;
  const blurb = String(book.blurb_ko ?? "");

  // 대량(100권+) 대응: 제목 검색 + 정렬 + 스크롤 목록(과다 시 상한)
  const q = query.trim().toLowerCase();
  const matched = q ? books.filter((b) => b.title.toLowerCase().includes(q)) : books.slice();
  if (sort === "title") matched.sort((a, b) => a.title.localeCompare(b.title, "ko"));
  if (sort === "shelf" && shelf) {
    // 바꾸는 중인 차례를 그대로 보여준다(저장 전에도 눈으로 확인할 수 있게)
    const rank = new Map(shelf.map((id, i) => [id, i]));
    matched.sort((a, b) => (rank.get(a.id) ?? 999) - (rank.get(b.id) ?? 999));
  }
  const LIST_CAP = 300;
  const shown = matched.slice(0, LIST_CAP);
  const overflow = matched.length - shown.length;

  return (
    <div className="book book--builder bk-2col">
      {/* 좌: 전자책 라이브러리(검색·정렬·스크롤 목록) */}
      <aside className="bk-lib">
        <div className="bk-lib__head">
          <b>전자책 {books.length}권</b>
          <button type="button" className="btn btn--primary bk-lib__new" disabled={busy} onClick={createBook}>＋ 새 전자책</button>
        </div>
        <input
          className="bk-lib__search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="제목 검색…"
        />
        <div className="bk-lib__sort">
          <button type="button" className={sort === "shelf" ? "on" : ""} onClick={() => setSort("shelf")} title="아이가 보는 차례">책장 차례</button>
          <button type="button" className={sort === "recent" ? "on" : ""} onClick={() => setSort("recent")}>최근순</button>
          <button type="button" className={sort === "title" ? "on" : ""} onClick={() => setSort("title")}>이름순</button>
          <span className="bk-lib__count">{matched.length}권</span>
        </div>
        {sort === "shelf" ? (
          <p className="bk-lib__shelfhint">
            ↑↓ 로 <b>아이가 보는 차례</b>를 바꿔요. 검색을 지우고 바꿔 주세요.
            {shelf ? " 아래 [웹에 올리기]를 눌러야 반영돼요." : ""}
          </p>
        ) : null}
        <ul className="bk-lib__list">
          {!inList ? (
            <li className="bk-lib__item bk-lib__item--on bk-lib__item--new">
              <span className="bk-lib__t">{title || "새 전자책"}</span>
              <span className="bk-lib__m">저장 안 됨</span>
            </li>
          ) : null}
          {shown.map((b) => (
            <li key={b.id} className={`bk-lib__item${b.id === bookId ? " bk-lib__item--on" : ""}`}>
              <button type="button" className="bk-lib__open" onClick={() => void switchBook(b.id)} title={b.title}>
                {/* 표지 썸네일 — 어떤 책에 표지가 없는지 목록에서 바로 보인다 */}
                <span className="bk-lib__cover">
                  {libCover(b) ? <img src={libCover(b)} alt="" draggable={false} /> : <span>표지 없음</span>}
                </span>
                <span className="bk-lib__t">{b.title || "(제목 없음)"}</span>
                <span className="bk-lib__m">{b.pages}쪽</span>
              </button>
              {sort === "shelf" && !q ? (
                <span className="bk-lib__ord">
                  <button type="button" title="앞으로" disabled={busy} onClick={() => moveBook(b.id, -1)}>↑</button>
                  <button type="button" title="뒤로" disabled={busy} onClick={() => moveBook(b.id, 1)}>↓</button>
                </span>
              ) : null}
              <button type="button" className="bk-lib__del" title="삭제" disabled={busy} onClick={() => void removeBook(b.id, b.title)}>✕</button>
            </li>
          ))}
          {overflow > 0 ? <li className="bk-lib__more">검색으로 좁혀 보세요 — {overflow}권 더 있음</li> : null}
          {books.length === 0 ? <li className="bk-lib__more">아직 전자책이 없어요. ＋ 새 전자책</li> : null}
        </ul>
        {/* 🛟 되살릴 수 있는 책 — 파일은 남았는데 목록에 없는 것 */}
        {lost.length > 0 ? (
          <div className="bk-lib__lost">
            <b>🛟 되살릴 수 있는 책 {lost.length}권</b>
            <p className="bk-lib__losthint">
              올린 그림·읽어주기는 <b>그대로 남아 있어요.</b> 목록에서만 사라진 거예요.
            </p>
            <ul>
              {lost.map((b) => (
                <li key={b.id}>
                  <span className="bk-lib__lostname">{b.id}</span>
                  <span className="bk-lib__lostwhat">{recoverSay(b)}</span>
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={busy}
                    onClick={() => recoverBook(b.id)}
                  >
                    📖 이 파일들로 되살리기
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </aside>

      {/* 우: 편집 */}
      <div className="bk-main">
        <div className="bk-bar adminbar">
          <span className="bk-cur" title={title}>
            {title || "새 전자책"}
            {!inList ? " · 저장 안 됨" : dirty ? " · ● 변경됨" : ""}
          </span>
          <span className="adminbar__actions">
            <button type="button" className="btn" onClick={exportBook}>내보내기</button>
          </span>
        </div>
        {undo ? (
          <p className="console__publish bk-undo">
            🗑 ‘{undo.label}’ 삭제됨 ·{" "}
            <button type="button" className="btn btn--sm" disabled={busy} onClick={() => void undoDelete()}>
              ↩ 되돌리기
            </button>
          </p>
        ) : null}
        {msg ? <p className="console__publish">{msg}</p> : null}

        {/* 💾 이 기기에 저장해 둔 작업 — 불러올지 버릴지는 운영자가 정한다.
            저절로 덮어쓰면 방금 고친 게 옛것으로 되돌아가는 사고가 난다. */}
        {draft ? (
          <div className="bk-draft">
            <span>
              💾 이 기기에 저장해 둔 작업이 있어요 <b>({draftAgeText(draft.at)})</b>
            </span>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => {
                if (!window.confirm("저장해 둔 작업을 불러올까요? 지금 화면의 내용은 사라져요.")) return;
                setBook(draft.doc);
                setManifest((m) => ({ ...m, assets: { ...m.assets, ...draft.assets } }));
                setIdx(0);
                setDirty(true);
                setMsg("저장해 둔 작업을 불러왔어요. 확인하고 [웹에 올리기]를 눌러 주세요.");
              }}
            >
              불러오기
            </button>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => {
                if (!window.confirm("저장해 둔 작업을 버릴까요? 되돌릴 수 없어요.")) return;
                clearBookDraft(bookId);
                setDraft(null);
              }}
            >
              버리기
            </button>
          </div>
        ) : null}

      {/* 다 읽은 뒤 이어질 굿즈 — 코드가 아니라 여기서 고른다 */}
      <BookGoodsPicker
        selected={normalizeBookGoods(book.goods)}
        onChange={(ids) => mutateBook({ goods: ids })}
      />

      {/* 1. 전자책 제목 */}
      <section className="bk-sec">
        <h3 className="bk-sec__h">1. 전자책 제목</h3>
        <div className="bk-grid2">
          <label>
            제목(한국어)
            <input value={String(book.title_ko ?? "")} placeholder="그레그와 병아리의 경주" onChange={(e) => mutateBook({ title_ko: e.target.value })} />
          </label>
          <label>
            제목(English·선택)
            <input value={String(book.title_en ?? "")} placeholder="Greg & Chick's Race" onChange={(e) => mutateBook({ title_en: e.target.value })} />
          </label>
        </div>
        <div className="bk-access">
          <label className="bk-check">
            <input
              type="checkbox"
              checked={access === "gated"}
              onChange={(e) => mutateBook({ access: e.target.checked ? "gated" : "full" })}
            />
            일부만 무료 공개(잠금)
          </label>
          {access === "gated" ? (
            <label className="bk-inline">
              무료 공개 쪽수
              <input
                type="number"
                min={1}
                max={Math.max(1, pages.length)}
                value={previewPages}
                onChange={(e) => mutateBook({ previewPages: Math.max(1, Number(e.target.value) || 1) })}
              />
            </label>
          ) : null}
        </div>
        <div className="bk-viewmode">
          <span className="bk-viewmode__label">📖 페이지 보기</span>
          {([
            { id: "spread", label: "두 장씩 (넓은 화면)", hint: "데스크톱은 두 쪽, 폰은 자동으로 한 쪽" },
            { id: "single", label: "한 장씩", hint: "어디서나 한 쪽씩" },
          ] as const).map((m) => (
            <button
              key={m.id}
              type="button"
              className={(String(book.viewMode ?? "spread") === m.id) ? "on" : ""}
              title={m.hint}
              onClick={() => mutateBook({ viewMode: m.id === "spread" ? undefined : m.id })}
            >
              {m.label}
            </button>
          ))}
        </div>
        {inList ? (
          <div className="bk-bookqr">
            <p className="bk-sec__sub">📕 <b>책 표지용 QR</b> — 스캔하면 이 전자책이 바로 열려요(표지·마케팅물에).</p>
            <QrPrint path={`/book?id=${encodeURIComponent(bookId)}`} title={title || "전자책"} caption="휴대폰으로 스캔하면 전자책이 열려요" />
          </div>
        ) : null}
      </section>

      {/* 2. 표지 — 아이가 고르기 화면에서 실제로 보는 것 */}
      <section className="bk-sec">
        <h3 className="bk-sec__h">
          2. 표지 <small>· 아이들이 &lsquo;전자책 고르기&rsquo; 화면에서 보는 그림</small>
        </h3>
        <div className="bk-cover">
          {/* 왼쪽: 아이 화면과 같은 모양으로 미리보기 — 올린 그림이 어떻게 보일지 여기서 끝난다 */}
          <div className="bk-cover__preview">
            <div className="bk-cover__book">
              {coverUrl ? (
                // 아이 화면과 **같은 함수**로 담는다 — 여기만 빼먹어서 위치를 바꿔도 안 바뀌었다
                <img src={absArtUrl(coverUrl)} alt="" draggable={false} style={coverImageStyle(coverStyleOf(book))} />
              ) : (
                <span className="bk-cover__auto">{title || "제목을 입력하세요"}</span>
              )}
              <span className="bk-cover__spine" />
              <span className="bk-cover__edge" />
              <span className="bk-cover__pages">{pages.length}쪽</span>
            </div>
            <b className="bk-cover__t">{title || "(제목 없음)"}</b>
            {normalizeBlurb(blurb) ? <span className="bk-cover__b">{normalizeBlurb(blurb)}</span> : null}
            <span className="bk-cover__cta">펼쳐 보기</span>
          </div>

          <div className="panel bk-cover__form">
            {coverSpec ? (
              <SlotUploader
                slotKey={coverKey}
                label="표지 그림 (파일 찾기·끌어놓기)"
                spec={coverSpec}
                entry={coverEntry}
                onChange={(_k, entry) => setEntry(coverKey, entry)}
                alt={title ? `${title} 표지` : undefined}
              />
            ) : null}
            <p className="zone__tip">
              {coverEntry
                ? "✅ 올린 표지가 고르기 화면에 나와요."
                : coverFallbackUrl
                  ? `표지를 안 올려도 괜찮아요 — ‘표지로 사용’ 표시한 ${coverPage} 쪽 그림이 자동으로 쓰여요.`
                  : "표지 그림도, ‘표지로 사용’ 표시한 페이지도 없어요. 지금은 제목만 그려진 표지가 나와요."}
            </p>
            {/* 카드는 3:4 인데 표지 비율은 제각각이라 잘린다 — 어디를 보여줄지 여기서 정한다.
                미리보기는 아이 화면과 **같은 함수·같은 비율**을 쓴다(coverImageStyle). */}
            {coverUrl ? (
              <CoverFramePicker
                url={absArtUrl(coverUrl)!}
                fit={coverStyleOf(book).coverFit}
                pos={coverStyleOf(book).coverPos}
                onChange={(patch) => mutateBook(patch)}
              />
            ) : null}
            <label>
              한 줄 소개 <small>(제목 아래 · {BLURB_MAX}자까지)</small>
              <input
                value={blurb}
                maxLength={BLURB_MAX}
                placeholder="달빛 아래, 그레그와 병아리가 달리기를 해요"
                onChange={(e) => mutateBook({ blurb_ko: e.target.value })}
              />
            </label>
          </div>
        </div>
      </section>

      {/* 3. 페이지 목록 + 추가/삭제/순서 */}
      <section className="bk-sec">
        <h3 className="bk-sec__h">3. 전자책 페이지 <small>· 클릭해 편집, 순서·삭제는 칩의 버튼으로</small></h3>
        <div className="bk-pages">
          {pages.map((p, i) => {
            const id = String(p.id ?? `p${i}`);
            const isCover = p.cover === true;
            return (
              <div key={id} className={`bk-chip${i === safeIdx ? " bk-chip--on" : ""}`}>
                <button type="button" className="bk-chip__sel" onClick={() => setIdx(i)}>
                  <span className="bk-chip__no">{i + 1}</span>
                  <span className="bk-chip__id">{isCover ? "표지" : id}</span>
                </button>
                <div className="bk-chip__ctl">
                  <button type="button" title="앞으로" onClick={() => movePage(i, -1)} disabled={i === 0}>▲</button>
                  <button type="button" title="뒤로" onClick={() => movePage(i, 1)} disabled={i === pages.length - 1}>▼</button>
                  <button type="button" className="bk-chip__del" title="삭제" onClick={() => deletePage(i)}>✕</button>
                </div>
              </div>
            );
          })}
          <button type="button" className="bk-add" onClick={addPage}>＋ 페이지 추가</button>
          {/* 표지는 고르기 화면에서 아이가 책을 알아보는 유일한 단서다 — 만드는 길을 눈에 띄게 둔다 */}
          <button type="button" className="bk-add bk-add--cover" onClick={addCoverPage} title="맨 앞에 표지 페이지를 넣어요">
            ＋ 표지 페이지
          </button>
        </div>
      </section>

      {/* 선택 페이지 편집 */}
      {pages.length === 0 ? (
        <p className="zone__tip">＋ 페이지 추가로 첫 페이지를 만들어 보세요.</p>
      ) : (
        <section className="bk-sec">
          <h3 className="bk-sec__h">
            4. 페이지 편집 <small>· {safeIdx + 1} / {pages.length} · {pageId}</small>
          </h3>
          <PageAspect
            aspect={aspect}
            onSet={(v) => mutatePage({ aspect: v === DEFAULT_ASPECT ? undefined : v })}
            onApplyAll={() =>
              setBook((b) => {
                if (!b) return b;
                const v = aspect === DEFAULT_ASPECT ? undefined : aspect;
                setDirty(true);
                return { ...b, pages: (b.pages as Page[]).map((p) => ({ ...p, aspect: v })) };
              })
            }
          />
          {/* 어느 말로 볼지 — 의성어는 말마다 길이가 딴판이라 자리도 따로 정한다.
              여기서 고른 말의 자리가 아래 캔버스에서 옮겨진다. */}
          <div className="book__lang seg" role="group" aria-label="미리보기 언어">
            <button
              type="button"
              className="seg__btn"
              aria-pressed={previewLang === "ko"}
              onClick={() => setPreviewLang("ko")}
            >
              한국어
            </button>
            <button
              type="button"
              className="seg__btn"
              aria-pressed={previewLang === "en"}
              onClick={() => setPreviewLang("en")}
            >
              English
            </button>
            <span className="hint">
              {previewLang === "en"
                ? motionEn
                  ? "영어 의성어 자리를 옮겨요"
                  : "영어 의성어를 안 적어서 한글이 보여요 — 옮기면 영어 자리로 저장돼요"
                : "한글 의성어 자리를 옮겨요"}
            </span>
          </div>
          <div className="book__stage">
            <div
              ref={canvasRef}
              className="book__canvas"
              style={{ aspectRatio: aspect }}
              onPointerMove={onMove}
              onPointerUp={() => setDrag(null)}
              onPointerLeave={() => setDrag(null)}
            >
              {imageUrl ? (
                <img className="book__bg" src={imageUrl} alt="" draggable={false} />
              ) : (
                <span className="book__dummy">{pageId} · 그림 준비중</span>
              )}
              {video || hasVideoFile ? <span className="book__videobadge">▶️ 동영상</span> : null}

              {motionText ? (
                <span
                  className={
                    "book__chip book__chip--motion" + (motionPlaced ? "" : " book__chip--unplaced")
                  }
                  title={
                    motionPlaced
                      ? "끌어서 자리를 옮겨요"
                      : "아직 자리를 안 정했어요 — 아이 화면에서는 기본 자리에 나와요."
                  }
                  style={{ left: `${motionPos.x * 100}%`, top: `${motionPos.y * 100}%` }}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    setDrag("motion");
                  }}
                >
                  {motionText}
                </span>
              ) : null}

              {subtitle ? (
                <span
                  className={
                    "book__chip book__chip--subtitle" +
                    (subtitlePlaced ? "" : " book__chip--unplaced")
                  }
                  title={
                    subtitlePlaced
                      ? "끌어서 자리를 옮겨요"
                      : "아직 자리를 안 정했어요 — 아이 화면에서는 그림 아래에 나와요. 끌어 놓으면 그림 위로 올라가요."
                  }
                  style={{ left: `${subtitlePos.x * 100}%`, top: `${subtitlePos.y * 100}%` }}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    setDrag("subtitle");
                  }}
                >
                  {subtitle}
                </span>
              ) : null}
            </div>

            <div className="panel">
              {spec ? (
                <SlotUploader
                  slotKey={imageKey}
                  label="페이지 그림 (파일 찾기·끌어놓기)"
                  spec={spec}
                  entry={mediaEntry(imageKey, legacy.image)}
                  onChange={setMedia(imageKey, legacy.image)}
                  alt={subtitle || undefined}
                />
              ) : null}

              <PageArSection
                bookId={bookId}
                pageId={pageId}
                pageLabel={`${safeIdx + 1}쪽`}
                pageImageUrl={imageUrl}
                pageAspect={aspect}
                pageVideo={video}
                assets={manifest.assets}
                setEntry={setEntry}
              />
              {videoSpec ? (
                <SlotUploader
                  slotKey={videoKey}
                  label="페이지 동영상 파일(mp4)"
                  spec={videoSpec}
                  entry={mediaEntry(videoKey, legacy.video)}
                  onChange={setMedia(videoKey, legacy.video)}
                  kind="video"
                />
              ) : null}
              <label className="bk-check">
                <input type="checkbox" checked={page.cover === true} onChange={(e) => mutatePage({ cover: e.target.checked })} />
                표지로 사용
              </label>
              <label>
                동영상(유튜브 링크·선택)
                <input value={video} placeholder="https://youtu.be/… (위 파일 업로드가 있으면 그쪽 우선)" onChange={(e) => mutatePage({ video: e.target.value })} />
              </label>
              {/* 그림 안에 글이 박혀 있는 원고(스캔본·완성 일러스트)를 손으로 다시 치지 않게.
                  채우기만 하고 저장은 운영자가 한다. */}
              <div className="bk-ocr">
                <button type="button" className="btn btn--sm" disabled={ocrBusy} onClick={() => void readTextFromImage()}>
                  {ocrBusy ? "읽는 중…" : "🔤 그림에서 글자 읽기"}
                </button>
                <span className="hint">아래 네 칸(자막·의성어 한/영)을 채워요. 확인하고 고친 뒤 저장하세요.</span>
              </div>
              <label>
                자막(subtitle_ko)
                <input value={subtitle} onChange={(e) => mutatePage({ subtitle_ko: e.target.value })} />
              </label>
              <label>
                자막(English·선택)
                <input value={String(page.subtitle_en ?? "")} onChange={(e) => mutatePage({ subtitle_en: e.target.value })} />
              </label>
              <label>
                모션텍스트(의성어)
                <input value={motionText} onChange={(e) => mutatePage({ motionText: e.target.value })} />
              </label>
              <label>
                모션텍스트(English·선택)
                <input
                  value={String(page.motionText_en ?? "")}
                  placeholder="비우면 위 한글 의성어를 그대로 써요"
                  onChange={(e) => mutatePage({ motionText_en: e.target.value })}
                />
              </label>
              <label>
                대체텍스트(alt_ko)
                <input value={String(page.alt_ko ?? "")} onChange={(e) => mutatePage({ alt_ko: e.target.value })} />
              </label>

              {/* 🔊 음성은 **자막을 읽어 만든다**(koText/enText). 그래서 자막 아래에 둔다 —
                  작업 차례가 글 → 소리다. 위에 있으면 아직 없는 글로 음성을 만들려다 막힌다. */}
              {audioSpec ? (
                <NarrationSection
                  audioSpec={audioSpec}
                  koKey={audioKoKey}
                  enKey={audioEnKey}
                  koLegacy={legacy.ko}
                  enLegacy={legacy.en}
                  koText={subtitle}
                  enText={String(page.subtitle_en ?? "")}
                  assets={manifest.assets}
                  setEntry={setEntry}
                />
              ) : null}
              <div className="book__resets">
                <button type="button" onClick={() => mutatePage({ subtitlePos: undefined })}>자막 위치 초기화(하단)</button>
                <button type="button" onClick={() => mutatePage({ motionPos: undefined })}>모션 위치 초기화(한글)</button>
                {page.motionPos_en ? (
                  <button type="button" onClick={() => mutatePage({ motionPos_en: undefined })}>
                    모션 위치 초기화(영어)
                  </button>
                ) : null}
              </div>
              <p className="zone__tip">
                칩을 드래그해 위치를 옮기세요. 동영상 링크가 있으면 뷰어에서 ▶️ 버튼으로 재생돼요(자동재생 안 함).
              </p>
            </div>
          </div>
        </section>
      )}

      </div>
    </div>
  );
}
